import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { format, addDays, differenceInDays, startOfWeek, endOfWeek, parseISO } from 'date-fns';

// ============== TYPES ==============

export interface ScheduledWorkOrder {
  id: string;
  order_number: string;
  product_name: string;
  customer_name: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'urgent' | 'high' | 'normal' | 'low';
  quantity: number;
  due_date: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  // Calculated fields
  start_date: string;
  end_date: string;
  duration_days: number;
  is_overdue: boolean;
  progress: number;
  order_type: string;
}

export interface ScheduleDay {
  date: string;
  dayName: string;
  isWeekend: boolean;
  isToday: boolean;
  workOrders: ScheduledWorkOrder[];
}

export interface ScheduleStats {
  totalOrders: number;
  completedOrders: number;
  inProgressOrders: number;
  pendingOrders: number;
  overdueOrders: number;
  completionRate: number;
}

// ============== QUERIES ==============

export function useScheduledWorkOrders(dateFrom?: string, dateTo?: string) {
  const from = dateFrom || format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const to = dateTo || format(addDays(endOfWeek(new Date(), { weekStartsOn: 1 }), 14), 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['scheduled-work-orders', from, to],
    queryFn: async () => {
      // Get work orders with relevant date info
      // Query work orders
      const { data, error } = await api
        .from('work_orders')
        .select('id, order_number, product_name, order_type, status, priority, quantity, due_date, scheduled_date, started_at, completed_at, created_at, customer_id')
        .eq('is_active', true)
        .neq('status', 'cancelled')
        .order('scheduled_date', { ascending: true });

      // Get customer names separately (apiClient no soporta embeds)
      const customerIds = [...new Set((data || []).map((wo: any) => wo.customer_id).filter(Boolean))];
      const customersMap: Record<string, string> = {};
      if (customerIds.length > 0) {
        const { data: customers } = await api.from('customers').select('id, name').in('id', customerIds);
        (customers || []).forEach((c: any) => { customersMap[c.id] = c.name; });
      }

      // Add customer_name to each work order
      const dataWithCustomers = (data || []).map((wo: any) => ({
        ...wo,
        customer_name: customersMap[wo.customer_id] || null,
      }));

      const resultData = dataWithCustomers;
      // Filter by date range
      const filteredData = resultData.filter((wo: any) => {
        const d = wo.scheduled_date || wo.due_date || wo.started_at || wo.created_at;
        return d && d >= from;
      });

      if (error) throw error;

      const today = format(new Date(), 'yyyy-MM-dd');

      // Process work orders
      return filteredData.map((wo: any) => {
        // Determine start and end dates
        const startDate = wo.scheduled_date
          ? format(parseISO(wo.scheduled_date), 'yyyy-MM-dd')
          : wo.started_at
            ? format(parseISO(wo.started_at), 'yyyy-MM-dd')
            : format(parseISO(wo.created_at), 'yyyy-MM-dd');

        const endDate = wo.completed_at
          ? format(parseISO(wo.completed_at), 'yyyy-MM-dd')
          : wo.due_date || format(addDays(parseISO(startDate), 7), 'yyyy-MM-dd');

        const durationDays = Math.max(1, differenceInDays(parseISO(endDate), parseISO(startDate)) + 1);

        // Calculate progress
        let progress = 0;
        if (wo.status === 'completed') {
          progress = 100;
        } else if (wo.status === 'in_progress') {
          if (wo.started_at && wo.due_date) {
            const totalDuration = differenceInDays(parseISO(wo.due_date), parseISO(wo.started_at));
            const elapsed = differenceInDays(new Date(), parseISO(wo.started_at));
            progress = Math.min(90, Math.max(10, (elapsed / totalDuration) * 100));
          } else {
            progress = 50;
          }
        }

        return {
          id: wo.id,
          order_number: wo.order_number,
          product_name: wo.product_name,
          order_type: wo.order_type,
          customer_name: wo.customer_name || null,
          status: wo.status,
          priority: wo.priority,
          quantity: wo.quantity,
          due_date: wo.due_date,
          started_at: wo.started_at,
          completed_at: wo.completed_at,
          created_at: wo.created_at,
          start_date: startDate,
          end_date: endDate,
          duration_days: durationDays,
          is_overdue: wo.due_date && wo.due_date < today && wo.status !== 'completed',
          progress,
        } as ScheduledWorkOrder;
      });
    },
  });
}

export function useScheduleCalendar(dateFrom: string, dateTo: string) {
  const { data: workOrders, ...rest } = useScheduledWorkOrders(dateFrom, dateTo);

  // Generate calendar days
  const days: ScheduleDay[] = [];
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const today = format(new Date(), 'yyyy-MM-dd');

  let currentDate = parseISO(dateFrom);
  const endDate = parseISO(dateTo);

  while (currentDate <= endDate) {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    const dayOfWeek = currentDate.getDay();

    // Find work orders for this day
    const dayWorkOrders = (workOrders || []).filter(wo => {
      return wo.start_date <= dateStr && wo.end_date >= dateStr;
    });

    days.push({
      date: dateStr,
      dayName: dayNames[dayOfWeek],
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      isToday: dateStr === today,
      workOrders: dayWorkOrders,
    });

    currentDate = addDays(currentDate, 1);
  }

  return { data: days, workOrders, ...rest };
}

export function useScheduleStats(dateFrom?: string, dateTo?: string) {
  const { data: workOrders } = useScheduledWorkOrders(dateFrom, dateTo);

  const stats: ScheduleStats = {
    totalOrders: 0,
    completedOrders: 0,
    inProgressOrders: 0,
    pendingOrders: 0,
    overdueOrders: 0,
    completionRate: 0,
  };

  if (workOrders) {
    stats.totalOrders = workOrders.length;
    stats.completedOrders = workOrders.filter(wo => wo.status === 'completed').length;
    stats.inProgressOrders = workOrders.filter(wo => wo.status === 'in_progress').length;
    stats.pendingOrders = workOrders.filter(wo => wo.status === 'pending').length;
    stats.overdueOrders = workOrders.filter(wo => wo.is_overdue).length;
    stats.completionRate = stats.totalOrders > 0
      ? (stats.completedOrders / stats.totalOrders) * 100
      : 0;
  }

  return stats;
}

// ============== HELPER FUNCTIONS ==============

export function getPriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    urgent: 'bg-red-500',
    high: 'bg-orange-500',
    normal: 'bg-blue-500',
    low: 'bg-gray-400',
  };
  return colors[priority] || 'bg-blue-500';
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-gray-200 text-gray-700',
    in_progress: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
  };
  return colors[status] || 'bg-gray-200 text-gray-700';
}

export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Pendiente',
    in_progress: 'En progreso',
    completed: 'Completada',
    cancelled: 'Cancelada',
  };
  return labels[status] || status;
}

export function getPriorityLabel(priority: string): string {
  const labels: Record<string, string> = {
    urgent: 'Urgente',
    high: 'Alta',
    normal: 'Normal',
    low: 'Baja',
  };
  return labels[priority] || priority;
}
